// frontend/src/components/TableSettings.js
import React, { useState, useEffect, useCallback } from 'react';
import { LoadingButton } from '@mui/lab';
import {
    List, ListItem, ListItemText, Checkbox, ListItemButton, ListItemIcon, Box, Typography, Grid, Paper, Button,
    Divider // Hinzugefügt
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'; // Für besseres Drag-Handle

// isMobile als Prop hinzugefügt
function TableSettings({ savePreferences, initialColumns, allColumnsConfig, isSavingPreferences, isMobile }) {
  const [availableColumns, setAvailableColumns] = useState([]);
  const [selectedColumns, setSelectedColumns] = useState([]);

  useEffect(() => {
    if (initialColumns && allColumnsConfig) {
        const initialSelectedIds = new Set(initialColumns.map(col => col.id)); // Set für schnellere Suche
        const initialAvailable = allColumnsConfig.filter(col => !initialSelectedIds.has(col.id));
        // Sortiere verfügbare Spalten alphabetisch
        initialAvailable.sort((a, b) => a.label.localeCompare(b.label));
        setAvailableColumns(initialAvailable);
        // Behalte die Reihenfolge der initial ausgewählten Spalten bei
        setSelectedColumns(initialColumns);
    }
  }, [initialColumns, allColumnsConfig]);

  const handleVisibilityChange = useCallback((columnId) => {
    const columnToMove = allColumnsConfig.find(col => col.id === columnId);
    if (!columnToMove) return;

    const isCurrentlySelected = selectedColumns.some(col => col.id === columnId);

    if (isCurrentlySelected) {
      // Von Selected zu Available
      setSelectedColumns(prev => prev.filter(col => col.id !== columnId));
      setAvailableColumns(prev => [...prev, columnToMove].sort((a, b) => a.label.localeCompare(b.label)));
    } else {
      // Von Available zu Selected (ans Ende hinzufügen)
      setAvailableColumns(prev => prev.filter(col => col.id !== columnId));
      setSelectedColumns(prev => [...prev, columnToMove]);
    }
  }, [allColumnsConfig, selectedColumns]); // selectedColumns als Abhängigkeit

  const handleOnDragEnd = useCallback((result) => {
    if (!result.destination) return;
    // Nur innerhalb der "selected" Liste verschieben
    if (result.source.droppableId === 'selected' && result.destination.droppableId === 'selected') {
        const items = Array.from(selectedColumns);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);
        setSelectedColumns(items);
    }
  }, [selectedColumns]);

  const handleSave = useCallback(() => {
    const columnIds = selectedColumns.map(col => col.id);
    savePreferences({ tableColumns: columnIds });
  }, [selectedColumns, savePreferences]);

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
        Tabellenspalten anpassen
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
          Wähle die Spalten aus, die in der Buchliste angezeigt werden sollen, und ändere ihre Reihenfolge per Drag & Drop (nur ausgewählte Spalten).
      </Typography>
      <DragDropContext onDragEnd={handleOnDragEnd}>
          <Grid container spacing={2} sx={{ opacity: isSavingPreferences ? 0.7 : 1 }}>
            {/* Verfügbare Spalten */}
            <Grid item xs={12} md={isMobile ? 12 : 6}> {/* Volle Breite auf Mobile */}
              <Paper elevation={2} sx={{ p: 1, height: isMobile ? 'auto' : 400, overflow: 'auto' }}>
                <Typography variant="subtitle1" sx={{ p: 1 }}>Verfügbare Spalten</Typography>
                <Divider sx={{ mb: 1 }} />
                <List dense>
                  {availableColumns.map((col) => (
                    <ListItem key={col.id} disablePadding>
                      <ListItemButton onClick={() => handleVisibilityChange(col.id)} dense disabled={isSavingPreferences}>
                        <ListItemIcon sx={{ minWidth: 'auto', mr: 1 }}>
                          <Checkbox edge="start" checked={false} tabIndex={-1} disableRipple />
                        </ListItemIcon>
                        <ListItemText primary={col.label} />
                      </ListItemButton>
                    </ListItem>
                  ))}
                  {availableColumns.length === 0 && <ListItem><ListItemText secondary="Alle Spalten ausgewählt." /></ListItem>}
                </List>
              </Paper>
            </Grid>

            {/* Ausgewählte Spalten (Droppable) */}
            <Grid item xs={12} md={isMobile ? 12 : 6}> {/* Volle Breite auf Mobile */}
              <Paper elevation={2} sx={{ p: 1, height: isMobile ? 'auto' : 400, overflow: 'auto' }}>
                <Typography variant="subtitle1" sx={{ p: 1 }}>Ausgewählte Spalten (Reihenfolge ändern)</Typography>
                <Divider sx={{ mb: 1 }} />
                <Droppable droppableId="selected">
                  {(provided) => (
                    <List dense ref={provided.innerRef} {...provided.droppableProps}>
                      {selectedColumns.map((col, index) => (
                        <Draggable key={col.id} draggableId={col.id} index={index}>
                          {(provided) => (
                            <ListItem
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              disablePadding
                              sx={{ mb: 0.5, backgroundColor: 'background.paper', borderRadius: 1 }}
                            >
                              <ListItemButton onClick={() => handleVisibilityChange(col.id)} dense disabled={isSavingPreferences}>
                                <ListItemIcon sx={{ minWidth: 'auto', mr: 1 }}>
                                  <Checkbox edge="start" checked={true} tabIndex={-1} disableRipple />
                                </ListItemIcon>
                                <ListItemText primary={col.label} />
                                <ListItemIcon sx={{ minWidth: 'auto', cursor: 'grab' }}>
                                    <DragIndicatorIcon fontSize="small" />
                                </ListItemIcon>
                              </ListItemButton>
                            </ListItem>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                      {selectedColumns.length === 0 && <ListItem><ListItemText secondary="Keine Spalten ausgewählt." /></ListItem>}
                    </List>
                  )}
                </Droppable>
              </Paper>
            </Grid>
          </Grid>
      </DragDropContext>
      <Box mt={2}>
        <LoadingButton
            variant="contained"
            color="primary"
            onClick={handleSave}
            loading={isSavingPreferences}
            loadingPosition="start"
            startIcon={<SaveIcon />}
            disabled={isSavingPreferences}
            fullWidth={isMobile} // Button über volle Breite auf Mobile
        >
            Einstellungen speichern
        </LoadingButton>
      </Box>
    </Box>
  );
}

export default TableSettings;
